import { useMemo } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './styles/App.css';
import { MuiThemeProvider } from '@material-ui/core';
import AbsenceSettingsPage from './components/pages/absenceSettingsPage';
import AbsenceCalendarPage from './components/pages/absenceCalendarPage';
import AbsenceRequestsPage from './components/pages/absenceRequestsPage';
import AbsenceReportPage from './components/pages/absenceReportPage';
import CyclesPage from './components/pages/cyclesPage';
import CycleDetailsPage from './components/pages/cycleDetailsPage';
import LevelingPage from './components/pages/levelingPage';
import ProtectedRoute from './components/shared/protectedRoute';
import AlertComponent from './components/shared/alertComponent';
import SidebarNavigation from './components/shared/sidebarNavigation';
import KadarTheme from './styles/theme';
import ScrollToTop from './components/shared/scrollToTop';
import SurveysPageView from './views/surveys';
import SurveySettingsPage from './components/pages/surveySettingsPage';
import SurveyReportPage from './components/pages/surveyReportPage';
import AttributesPage from './components/pages/attributesPage';
import SurveysHistoryPage from './components/pages/surveysHistoryPage';
import SurveyHistoryPage from './components/pages/surveyHistoryPage';
import CompareReportPage from './components/pages/compareReportPage';
import PeopleAbsencesPage from './components/pages/peopleAbsencesPage';
import PeopleActionPlansPage from './components/pages/peopleActionPlansPage';
import PeopleReviewCyclesPage from './components/pages/peopleReviewCyclesPage';
import PeopleRolesPage from './components/pages/peopleRolesPage';
import PeopleInsightsPageView from './views/peopleInsights';
import PeopleCoursesPage from './components/pages/peopleCoursesPage';
import PeopleNotesPage from './components/pages/peopleNotesPage';
import PeopleOneOnOnePageView from './views/peopleOneOnOne';
import PeopleCareerPageView from './views/peopleCareer';
import PeopleInfoPage from './components/pages/peopleInfoPage';
import PeopleDocumentsPage from './components/pages/peopleDocumentsPage';
import PeoplePage from './components/pages/peoplePage';
import LoginPage from './components/pages/loginPage';
import PageNotFound from './components/pages/pageNotFound';
import PublicJobUnavailable from './components/pages/publicJobUnavailable';
import Loader from './components/shared/loader';
import ForgottenPasswordPage from './components/pages/forgottenPasswordPage';
import ResetLinkSentPage from './components/pages/resetLinkSentPage';
import ResetPasswordPage from './components/pages/resetPasswordPage';
import TagsPage from './components/pages/tagsPage';
import TagCategoryPage from './components/pages/tagCategoryPage';
import CompanyOverviewPage from './components/pages/companyOverviewPage';
import FillSurveyPageView from './views/fillSurvey';
import PublicJobPage from './components/pages/publicJobPage';
import ApplyJobPage from './components/pages/applyJobPage';
import ThankYouPage from './components/pages/thankYouPage';
import OrganizationPageView from './views/organization';
import ActionPlansPageView from './views/actionPlans';
import ActionPlanDetailsPageView from './views/actionPlanDetails';
import PrivateNotesView from './views/privateNotes';
import AnnouncementsPage from './components/pages/announcementsPage';
import AnnouncementCategoryPage from './components/pages/announcementCategoryPage';
import OneOnOnePageView from './views/oneOnOne';
import OneOnOneDetailsPageView from './views/oneOnOneDetails';
import UnauthorizedAccessPage from './components/pages/unauthorizedAccessPage';
import CoursesPageView from './views/courses';
import CourseDetailsPageView from './views/courseDetails';
import CompetenceMapPageView from './views/competenceMap';
import SkillsMatrixPageView from './views/skillsMatrix';
import DashboardPage from './components/pages/dashboardPage';
import OneOnOneReportPageView from './views/oneOnOneReport';
import AttributeReportPage from './components/pages/attributeReportPage';
import SurveySummaryPage from './components/pages/surveySummaryPage';
import SurveySummaryDetailsPage from './components/pages/surveySummaryDetailsPage';
import OrganizationSettingsFeaturesPage from './components/pages/organizationSettingsFeaturesPage';
import OrganizationSettingsPermissionsPage from './components/pages/organizationSettingsPermissionsPage';
import OrganizationSettingsDocumentsPage from './components/pages/organizationSettingsDocumentsPage';
import OrganizationSettingsPeopleInfoPage from './components/pages/organizationSettingsPeopleInfoPage';
import SuccessionSettingsPage from './components/pages/successionSettingsPage';
import SuccessionKeyJobsPage from './components/pages/successionKeyJobsPage';
import SuccessionKeyJobPage from './components/pages/successionKeyJobPage';
import SuccessionPoolsPage from './components/pages/successionPoolsPage';
import SuccessionPoolPage from './components/pages/successionPoolPage';
import UpgradeRequiredDialog from './components/shared/upgradeRequiredDialog';
import ReleaseNotesPage from './components/pages/releaseNotesPage';
import JobTitlesPageView from './views/jobTitles';
import ChatButton from './components/shared/chatButton';
import PricingPlansPage from './components/pages/pricingPlansPage';
import JobsPage from './components/pages/jobsPage';
import SlackIntegrationPage from './components/pages/slackIntegrationPage';
import FreePlanLimitDialog from './components/shared/freePlanLimitDialog';
import SignUpPage from './components/pages/signUpPage';
import InstructionGuideDrawer from './components/shared/instructionGuideDrawer';
import NavigationSetter from './components/shared/navigationSetter';
import DivisionsPage from './components/pages/divisionsPage';
import JobDetailsPage from './components/pages/jobDetailsPage';
import JobPostingDetailsPage from './components/pages/jobPostingDetailsPage';
import CandidatesPage from './components/pages/candidatesPage';
import CandidateDetailsPage from './components/pages/candidateDetailsPage';
import LoginAuthenticationPage from './components/pages/loginAuthenticationPage';
import TwoFactorOptionsPage from './components/pages/twoFactorOptionsPage';
import DisableAuthenticationPage from './components/pages/disableAuthenticationPage';
import HeadcountReportPage from './components/pages/headcountReportPage';
import TimeToHireReportPage from './components/pages/timeToHireReportPage';
import { APP_PAGES } from './constants/pages';
import { PERMISSION_CODES } from './constants/permissions';
import { authSelector } from './store/selectors/authSelector';
import { accessControlSelector } from './store/selectors/accessControlSelector';
import { organizationSettingsSelector } from './store/selectors/organizationSettingsSelector';
import { useTranslations } from './utility/useTranslations';
import {
  prepareAccessControl,
  prepareOrgSettingsDefaultPath,
} from './utility/permissions';

const App = () => {
  const translations = useTranslations(APP_PAGES.APP);
  const { auth } = useSelector(authSelector);
  const accessControl = useSelector(accessControlSelector);

  const { organizationSettings, organizationMenuSetup, presets } = useSelector(
    organizationSettingsSelector
  );

  const isAuthenticated = !!auth.access;
  const canUsersSeeCourses = !!organizationSettings?.users_see_courses;
  const canUsersSeeAbsence = !!organizationSettings?.users_see_absence;
  const canUsersSeeRecruitment = organizationSettings?.users_see_recruitment;
  const canUsersSeeSuccession = organizationSettings?.users_see_succession;
  const aControl = useMemo(
    () => prepareAccessControl(accessControl),
    [accessControl]
  );

  return (
    <MuiThemeProvider theme={KadarTheme}>
      <BrowserRouter>
        <NavigationSetter />
        <ScrollToTop>
          <div className="App">
            <Loader />
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                    presets={presets}
                  >
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/2fa/options"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                  >
                    <TwoFactorOptionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/2fa/disable"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                  >
                    <DisableAuthenticationPage />
                  </ProtectedRoute>
                }
              />
              <Route path="action-plans">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PLANS_VIEW]}
                    >
                      <ActionPlansPageView />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":actionPlanId/details"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PLANS_VIEW]}
                    >
                      <ActionPlanDetailsPageView />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path="my-private-notes"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                  >
                    <PrivateNotesView />
                  </ProtectedRoute>
                }
              />
              <Route path="announcements">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.ANNOUNCEMENTS_VIEW,
                      ]}
                    >
                      <AnnouncementsPage />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":categoryKey"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.ANNOUNCEMENTS_VIEW,
                      ]}
                    >
                      <AnnouncementCategoryPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="1-1">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.ONE_ON_ONE_VIEW]}
                    >
                      <OneOnOnePageView />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":oneOnOneId/details"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.ONE_ON_ONE_VIEW]}
                    >
                      <OneOnOneDetailsPageView />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="learning">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeCourses}
                      requiredPermissions={[PERMISSION_CODES.LEARNING_VIEW]}
                    >
                      <CoursesPageView />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":courseId"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeCourses}
                      requiredPermissions={[PERMISSION_CODES.LEARNING_VIEW]}
                    >
                      <CourseDetailsPageView />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="cycles">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.CYCLES_VIEW]}
                    >
                      <CyclesPage />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":cycleId/details"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.CYCLES_VIEW]}
                    >
                      <CycleDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":cycleId/reviews/:surveyId/details"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.CYCLES_VIEW]}
                    >
                      <SurveySettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":cycleId/reviews/:surveyId/report"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.CYCLES_VIEW]}
                    >
                      <SurveyReportPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="surveys">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.SURVEYS_VIEW_ALL,
                        PERMISSION_CODES.SURVEYS_VIEW_CREATED,
                      ]}
                    >
                      <SurveysPageView />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":surveyId/settings"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.SURVEYS_VIEW_ALL,
                        PERMISSION_CODES.SURVEYS_VIEW_CREATED,
                      ]}
                    >
                      <SurveySettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":surveyId/report"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.SURVEYS_VIEW_ALL,
                        PERMISSION_CODES.SURVEYS_VIEW_CREATED,
                      ]}
                    >
                      <SurveyReportPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path="attributes"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                    requiredPermissions={[PERMISSION_CODES.ATTRIBUTES_VIEW]}
                  >
                    <AttributesPage />
                  </ProtectedRoute>
                }
              />
              <Route path="survey-history">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                    >
                      <SurveysHistoryPage />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":token"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                    >
                      <SurveyHistoryPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="reports">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_HEADCOUNT_VIEW,
                      ]}
                    >
                      <Navigate to="survey-summary" replace />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path="company-overview"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_OVERVIEW_VIEW,
                      ]}
                    >
                      <CompanyOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="headcount"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_HEADCOUNT_VIEW,
                      ]}
                    >
                      <HeadcountReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="absence"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      isFeatureActive={canUsersSeeAbsence}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_ABSENCE_VIEW,
                      ]}
                    >
                      <AbsenceReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="competence-map"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_COMPETENCE_MAP_VIEW,
                      ]}
                    >
                      <CompetenceMapPageView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="skills-matrix"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_SKILLS_MATRIX_VIEW,
                      ]}
                    >
                      <SkillsMatrixPageView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="general-mood"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_GENERAL_MOOD_VIEW,
                      ]}
                    >
                      <OneOnOneReportPageView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="attribute"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_ATTRIBUTE_VIEW,
                      ]}
                    >
                      <AttributeReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="compare"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      requiredPermissions={[
                        PERMISSION_CODES.REPORTS_COMPARE_VIEW,
                      ]}
                    >
                      <CompareReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="survey-summary">
                  <Route
                    element={
                      <ProtectedRoute
                        auth={auth}
                        accessControl={aControl}
                        organizationSettings={organizationSettings}
                      >
                        <SurveySummaryPage />
                      </ProtectedRoute>
                    }
                    index
                  />
                  <Route
                    path=":summaryId/details"
                    element={
                      <ProtectedRoute
                        auth={auth}
                        accessControl={aControl}
                        organizationSettings={organizationSettings}
                      >
                        <SurveySummaryDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>
              </Route>
              <Route
                path="levels-and-tracks"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                    requiredPermissions={[PERMISSION_CODES.LEVELING_VIEW]}
                  >
                    <LevelingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="job-titles"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                    requiredPermissions={[PERMISSION_CODES.JOB_TITLES_VIEW]}
                  >
                    <JobTitlesPageView />
                  </ProtectedRoute>
                }
              />
              <Route path="succession">
                <Route index element={<Navigate to="key-jobs" replace />} />
                <Route
                  path="key-jobs"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeSuccession}
                      requiredPermissions={[PERMISSION_CODES.SUCCESSION_MANAGE]}
                    >
                      <SuccessionKeyJobsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="key-jobs/:jobId"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeSuccession}
                      requiredPermissions={[PERMISSION_CODES.SUCCESSION_MANAGE]}
                    >
                      <SuccessionKeyJobPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="pools"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeSuccession}
                      requiredPermissions={[PERMISSION_CODES.SUCCESSION_MANAGE]}
                    >
                      <SuccessionPoolsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="pools/:poolId"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeSuccession}
                      requiredPermissions={[PERMISSION_CODES.SUCCESSION_MANAGE]}
                    >
                      <SuccessionPoolPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeSuccession}
                      requiredPermissions={[PERMISSION_CODES.SUCCESSION_MANAGE]}
                    >
                      <SuccessionSettingsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="jobs">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeRecruitment}
                      requiredPermissions={[PERMISSION_CODES.RECRUITMENT_VIEW]}
                    >
                      <JobsPage />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":jobId/posting"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeRecruitment}
                      requiredPermissions={[PERMISSION_CODES.RECRUITMENT_VIEW]}
                    >
                      <JobPostingDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":jobId/details/:stepId/:navSection"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeRecruitment}
                      requiredPermissions={[PERMISSION_CODES.RECRUITMENT_VIEW]}
                    >
                      <JobDetailsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="candidates">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeRecruitment}
                      requiredPermissions={[PERMISSION_CODES.RECRUITMENT_EDIT]}
                    >
                      <CandidatesPage />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":candidateId"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeRecruitment}
                      requiredPermissions={[PERMISSION_CODES.RECRUITMENT_EDIT]}
                    >
                      <CandidateDetailsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path="time-to-hire"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                    isFeatureActive={canUsersSeeRecruitment}
                    requiredPermissions={[PERMISSION_CODES.RECRUITMENT_VIEW]}
                  >
                    <TimeToHireReportPage />
                  </ProtectedRoute>
                }
              />
              <Route path="people">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                    >
                      <PeoplePage />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleCareerPageView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/insights"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleInsightsPageView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/review-cycles"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleReviewCyclesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/roles"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleRolesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/action-plans"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleActionPlansPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/learning"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeCourses}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleCoursesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/notes"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleNotesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/one-on-one"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleOneOnOnePageView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/absences"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeAbsence}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleAbsencesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/general-info"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.PROFILES_VIEW]}
                      isProfileRoute
                    >
                      <PeopleInfoPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id/documents"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.PROFILES_DOCUMENTS_VIEW,
                      ]}
                      isProfileRoute
                    >
                      <PeopleDocumentsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="absence">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      isFeatureActive={canUsersSeeAbsence}
                      requiredPermissions={[PERMISSION_CODES.ABSENCE_VIEW]}
                    >
                      <Navigate to="calendar" replace />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path="calendar"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      presets={presets}
                      isFeatureActive={canUsersSeeAbsence}
                      requiredPermissions={[PERMISSION_CODES.ABSENCE_VIEW]}
                    >
                      <AbsenceCalendarPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeAbsence}
                      requiredPermissions={[
                        PERMISSION_CODES.ABSENCE_SETTINGS_MANAGE,
                      ]}
                    >
                      <AbsenceSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="requests"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      isFeatureActive={canUsersSeeAbsence}
                      requiredPermissions={[PERMISSION_CODES.ABSENCE_VIEW]}
                    >
                      <AbsenceRequestsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path="divisions-and-departments"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                    requiredPermissions={[PERMISSION_CODES.DEPARTMENTS_VIEW]}
                  >
                    <DivisionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="org-chart"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                  >
                    <OrganizationPageView />
                  </ProtectedRoute>
                }
              />
              <Route path="tags">
                <Route
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.TAGS_VIEW]}
                    >
                      <TagsPage />
                    </ProtectedRoute>
                  }
                  index
                />
                <Route
                  path=":categoryId/people"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[PERMISSION_CODES.TAGS_VIEW]}
                    >
                      <TagCategoryPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="organization-settings">
                <Route
                  index
                  element={
                    <Navigate
                      to={prepareOrgSettingsDefaultPath(aControl)}
                      replace
                    />
                  }
                />
                <Route
                  path="features"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.SETTINGS_FEATURES_MANAGE,
                      ]}
                    >
                      <OrganizationSettingsFeaturesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="permissions"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.SETTINGS_PERMISSIONS_MANAGE,
                      ]}
                    >
                      <OrganizationSettingsPermissionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="documents"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.SETTINGS_DOCUMENTS_MANAGE,
                      ]}
                    >
                      <OrganizationSettingsDocumentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="profile-info"
                  element={
                    <ProtectedRoute
                      auth={auth}
                      accessControl={aControl}
                      organizationSettings={organizationSettings}
                      requiredPermissions={[
                        PERMISSION_CODES.SETTINGS_PERSON_FIELDS_INFO_MANAGE,
                      ]}
                    >
                      <OrganizationSettingsPeopleInfoPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path="release-notes"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                  >
                    <ReleaseNotesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="slack-integration"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                    requiredPermissions={[
                      PERMISSION_CODES.SETTINGS_SLACK_INTEGRATION_MANAGE,
                    ]}
                  >
                    <SlackIntegrationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="pricing-plans"
                element={
                  <ProtectedRoute
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                    requiredPermissions={[
                      PERMISSION_CODES.SETTINGS_SUBSCRIPTION_MANAGE,
                    ]}
                  >
                    <PricingPlansPage />
                  </ProtectedRoute>
                }
              />
              <Route path="sign-up" element={<SignUpPage />} />
              <Route
                path="fill-survey/:token"
                element={<FillSurveyPageView />}
              />
              <Route path="thank-you" element={<ThankYouPage />} />
              <Route path="/job/:token" element={<PublicJobPage />} />
              <Route path="/job/apply/:token" element={<ApplyJobPage />} />
              <Route
                path="login/authentication/:token?"
                element={<LoginAuthenticationPage />}
              />
              <Route path="login/:token?" element={<LoginPage />} />
              <Route
                path="forgotten-password"
                element={<ForgottenPasswordPage />}
              />
              <Route
                path="reset-link-sent/:email/:resend?"
                element={<ResetLinkSentPage />}
              />
              <Route
                path="set-password/:uid/:token/:name?"
                element={<ResetPasswordPage />}
              />
              <Route
                path="job-unavailable"
                element={<PublicJobUnavailable />}
              />
              <Route
                path="unauthorized-access"
                element={
                  <UnauthorizedAccessPage
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                  />
                }
              />
              <Route
                path="not-found"
                element={
                  <PageNotFound
                    auth={auth}
                    accessControl={aControl}
                    organizationSettings={organizationSettings}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/not-found" replace />} />
            </Routes>
            {isAuthenticated && (
              <SidebarNavigation
                translations={translations.sidebarNavigation}
                auth={auth}
                accessControl={aControl}
                organizationSettings={organizationSettings}
                organizationMenuSetup={organizationMenuSetup}
              />
            )}
            <AlertComponent />
            {isAuthenticated && (
              <UpgradeRequiredDialog accessControl={aControl} />
            )}
            {isAuthenticated && (
              <FreePlanLimitDialog accessControl={aControl} />
            )}
            {isAuthenticated && <InstructionGuideDrawer auth={auth} />}
            <ChatButton translations={translations} />
          </div>
        </ScrollToTop>
      </BrowserRouter>
    </MuiThemeProvider>
  );
};

export default App;
